import { useInfiniteQuery } from "@tanstack/react-query";
import axios from "axios";
import { useState, useMemo } from "react";
import { Accordion, Badge, Card, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart, faDollarSign, faUser, faStore, faHistory, faFileInvoiceDollar, faCalendar, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { useEmpresas } from "providers/EmpresaProvider";
import { useBreakpoints } from "providers/BreakpointsProvider";
import { FixedSizeList as List } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import { Link } from "react-router-dom";

interface HistoricoVenda {
    id: number;
    produtoId: number;
    empresaId: number;
    empresaNome: string;
    dataVenda: string;
    quantidade: number;
    valorUnitario: number;
    valorTotal: number;
    descontoValor: number;
    descontoPercentual: number;
    valorFinal: number;
    clienteNome?: string;
    clienteDocumento?: string;
    numeroVenda: string;
    tipoVenda: string;
    formaPagamento?: string;
    usuarioVendedor: string;
    lote?: string;
    validadeLote?: string;
    isDevolvido: boolean;
    observacoesVenda?: string;
}

interface HistoricoVendaResponse {
    data: HistoricoVenda[];
    total: number;
    hasMore: boolean;
    isSuccess: boolean;
    message: string;
}

interface HistoricoVendasTabProps {
    produtoId: number;
}

interface ItemProps {
    index: number;
    style: React.CSSProperties;
    data: {
        items: HistoricoVenda[];
        loadMoreItems: () => void;
        hasNextPage: boolean;
        isLoading: boolean;
    };
}

const VendaRow: React.FC<ItemProps> = ({ index, style, data }) => {
    const { items, loadMoreItems, hasNextPage, isLoading } = data;
    const { breakpoints } = useBreakpoints();
    const isMobile = breakpoints.down('md');

    // Se estamos próximos do fim e ainda há mais itens para carregar
    const isItemLoaded = (index: number) => !!items[index];

    if (!isItemLoaded(index) && hasNextPage) {
        if (index === items.length) {
            loadMoreItems();
            return (
                <div style={style} className="d-flex justify-content-center align-items-center">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Carregando...</span>
                </div>
            );
        }
        return null;
    }

    const venda = items[index];
    if (!venda) return null;

    const getBadgeVariant = (tipo: string) => {
        switch (tipo.toLowerCase()) {
            case "venda":
                return "success";
            case "devolução":
            case "devolucao":
                return "danger";
            case "troca":
                return "warning";
            default:
                return "secondary";
        }
    };

    const formatarMoeda = (valor: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    if (isMobile) {
        return (
            <div style={{
                ...style,
                background: venda.isDevolvido ? 'var(--phoenix-danger-bg-subtle)' : 'var(--phoenix-body-bg)',
                borderLeft: `3px solid ${venda.tipoVenda.toLowerCase() === 'venda' ? 'var(--phoenix-success)' :
                    venda.tipoVenda.toLowerCase().includes('devolução') || venda.tipoVenda.toLowerCase().includes('devolucao') ? 'var(--phoenix-danger)' :
                        venda.tipoVenda.toLowerCase() === 'troca' ? 'var(--phoenix-warning)' : 'var(--phoenix-secondary)'
                    }`
            }} className="px-3 py-2 border-bottom border-translucent">
                {/* Header compacto com informações principais */}
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-center gap-2">
                        <Badge
                            bg={getBadgeVariant(venda.tipoVenda)}
                            className="d-flex align-items-center gap-1 px-2 py-1"
                            style={{ fontSize: '0.75rem', fontWeight: '500' }}
                        >
                            <FontAwesomeIcon icon={faShoppingCart} size="sm" />
                            {venda.tipoVenda}
                        </Badge>
                        <Link
                            to={`/operacional/vendas?vendaId=${venda.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-none"
                            title={`Abrir venda #${venda.numeroVenda} em nova aba`}
                            style={{ transition: 'color 0.2s ease' }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = 'var(--phoenix-primary)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = '';
                            }}
                        >
                            <div className="d-flex align-items-center gap-1 text-body-tertiary small fw-medium">
                                <span>#{venda.numeroVenda}</span>
                                <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                            </div>
                        </Link>
                    </div>
                    <div className="text-end">
                        <div className="small text-body-tertiary">
                            {new Date(venda.dataVenda).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit'
                            })}
                        </div>
                    </div>
                </div>

                {/* Informações principais em linha */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center gap-2">
                        <div className="d-flex align-items-center gap-1 px-2 py-1 bg-body-secondary rounded">
                            <span className="fw-bold text-primary fs-6">{venda.quantidade}</span>
                            <span className="text-body-tertiary small">×</span>
                            <span className="small fw-medium">{formatarMoeda(venda.valorUnitario)}</span>
                        </div>
                    </div>
                    <div className="text-end">
                        <div className="fw-bold text-success fs-5 mb-0">
                            {formatarMoeda(venda.valorFinal)}
                        </div>
                        {venda.descontoValor > 0 && (
                            <small className="text-body-tertiary text-decoration-line-through">
                                {formatarMoeda(venda.valorTotal)}
                            </small>
                        )}
                    </div>
                </div>

                {/* Cliente em linha compacta */}
                <div className="d-flex align-items-center gap-2 mb-2">
                    <FontAwesomeIcon icon={faUser} className="text-body-tertiary" size="sm" />
                    <div className="flex-grow-1 min-width-0">
                        <div className="small fw-medium text-truncate">
                            {venda.clienteNome || "Cliente não informado"}
                        </div>
                        {venda.clienteDocumento && (
                            <div className="small text-body-tertiary text-truncate">
                                {venda.clienteDocumento}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer compacto */}
                <div className="d-flex justify-content-between align-items-center pt-2 border-top border-translucent">
                    <div className="d-flex align-items-center gap-1 flex-grow-1 min-width-0 me-2">
                        <FontAwesomeIcon icon={faStore} className="text-primary flex-shrink-0" size="sm" />
                        <small className="text-body fw-medium text-truncate">
                            {venda.empresaNome}
                        </small>
                    </div>
                    <div className="d-flex align-items-center gap-1 flex-shrink-0">
                        <FontAwesomeIcon icon={faUser} className="text-body-tertiary" size="sm" />
                        <small className="text-body-tertiary text-truncate" style={{ maxWidth: "80px" }}>
                            {venda.usuarioVendedor}
                        </small>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ ...style, minWidth: "800px" }} className="d-flex align-items-center border-bottom border-translucent px-2 py-2">
            <div className="flex-shrink-0" style={{ width: "85px" }}>
                <small>
                    {new Date(venda.dataVenda).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    })}
                </small>
            </div>

            <div className="flex-shrink-0 mx-1" style={{ width: "80px" }}>
                <div className="d-flex align-items-center gap-1">
                    <FontAwesomeIcon icon={faShoppingCart} className="text-success" size="sm" />
                    <Badge bg={getBadgeVariant(venda.tipoVenda)} pill className="fs-9">
                        {venda.tipoVenda}
                    </Badge>
                </div>
            </div>

            <div className="flex-shrink-0 mx-1 text-end" style={{ width: "50px" }}>
                <span className="fw-bold text-primary">
                    {venda.quantidade}
                </span>
            </div>

            <div className="flex-shrink-0 mx-1 text-end" style={{ width: "70px" }}>
                <small>{formatarMoeda(venda.valorUnitario)}</small>
            </div>

            <div className="flex-shrink-0 mx-1 text-end" style={{ width: "80px" }}>
                <span className="fw-bold text-success">
                    {formatarMoeda(venda.valorFinal)}
                </span>
                {venda.descontoValor > 0 && (
                    <div>
                        <small className="text-body-tertiary">
                            {formatarMoeda(venda.valorTotal)}
                        </small>
                    </div>
                )}
            </div>

            <div className="flex-grow-1 mx-1" style={{ minWidth: "120px" }}>
                <div className="d-flex align-items-center gap-1">
                    <FontAwesomeIcon icon={faUser} className="text-body-tertiary" size="sm" />
                    <small className="text-truncate" style={{ maxWidth: "150px" }}>
                        {venda.clienteNome || "Cliente não informado"}
                    </small>
                </div>
                {venda.clienteDocumento && (
                    <div>
                        <small className="text-body-tertiary text-truncate d-block" style={{ maxWidth: "150px" }}>
                            {venda.clienteDocumento}
                        </small>
                    </div>
                )}
            </div>

            <div className="flex-shrink-0 mx-1" style={{ width: "90px" }}>
                <div className="d-flex align-items-center gap-1">
                    <FontAwesomeIcon icon={faStore} className="text-body-tertiary" size="sm" />
                    <small className="text-truncate d-block" style={{ maxWidth: "80px" }}>
                        {venda.empresaNome}
                    </small>
                </div>
            </div>

            <div className="flex-shrink-0 mx-1" style={{ width: "70px" }}>
                <Link
                    to={`/operacional/vendas?vendaId=${venda.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                    title={`Abrir venda #${venda.numeroVenda} em nova aba`}
                    style={{ transition: 'color 0.2s ease' }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--phoenix-primary)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = '';
                    }}
                >
                    <div className="d-flex align-items-center gap-1">
                        <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-body-tertiary" size="sm" />
                        <small>#{venda.numeroVenda}</small>
                        <FontAwesomeIcon icon={faExternalLinkAlt} className="text-body-tertiary" size="xs" />
                    </div>
                </Link>
            </div>

            <div className="flex-shrink-0" style={{ width: "80px" }}>
                <small className="text-body-tertiary text-truncate d-block" style={{ maxWidth: "80px" }}>
                    {venda.usuarioVendedor}
                </small>
            </div>
        </div>
    );
};

const HistoricoVendasTab: React.FC<HistoricoVendasTabProps> = ({ produtoId }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { selectedIds: empresaSelecionadas } = useEmpresas();
    const { breakpoints } = useBreakpoints();
    const isMobile = breakpoints.down('md');

    const pageSize = 50; // Maior pageSize para melhor performance com virtualização
    const empresasQuery = empresaSelecionadas.join(',');

    const {
        data,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch
    } = useInfiniteQuery({
        queryKey: ["produto", "historico-vendas", produtoId, empresasQuery],
        queryFn: async ({ pageParam = 1 }) => {
            const { data } = await axios.get(
                `/api/produto/${produtoId}/historico-vendas?page=${pageParam}&pageSize=${pageSize}&empresas=${empresasQuery}`
            );
            return data as HistoricoVendaResponse;
        },
        getNextPageParam: (lastPage: HistoricoVendaResponse, allPages) => {
            return lastPage.hasMore ? allPages.length + 1 : undefined;
        },
        initialPageParam: 1,
        enabled: isExpanded && !!produtoId,
        staleTime: 5 * 60 * 1000, // 5 minutos
    });

    // Flatten all items from all pages
    const allVendas = useMemo(() => {
        return data?.pages?.flatMap(page => page.data) || [];
    }, [data]);

    const totalCount = data?.pages?.[0]?.total || 0;
    const totalVendido = useMemo(() => {
        return allVendas.reduce((sum, venda) => sum + venda.quantidade, 0);
    }, [allVendas]);

    const valorTotalVendido = useMemo(() => {
        return allVendas.reduce((sum, venda) => sum + venda.valorFinal, 0);
    }, [allVendas]);

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    const loadMoreItems = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="d-flex justify-content-center p-4">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Carregando histórico de vendas...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center p-4">
                    <p className="text-danger mb-3">Erro ao carregar histórico de vendas</p>
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => refetch()}
                    >
                        Tentar novamente
                    </button>
                </div>
            );
        }

        if (allVendas.length === 0) {
            return (
                <div className="text-center p-4 text-body-tertiary">
                    <FontAwesomeIcon icon={faShoppingCart} size="2x" className="mb-3" />
                    <p>Nenhuma venda encontrada para este produto</p>
                </div>
            );
        }

        // Calcula o número de itens (incluindo um extra para loading se necessário)
        const itemCount = hasNextPage ? allVendas.length + 1 : allVendas.length;

        // Header da tabela - com margem para compensar scrollbar e larguras reduzidas
        const tableHeader = (
            <div className="d-flex align-items-center border-bottom border-translucent bg-body-secondary px-2 py-2 fw-bold"
                style={{
                    marginRight: itemCount > Math.floor(400 / 80) ? "17px" : "0",
                    minWidth: "800px" // Força largura mínima para evitar quebra
                }}>
                <div style={{ width: "85px" }}>Data</div>
                <div className="mx-1" style={{ width: "80px" }}>Tipo</div>
                <div className="mx-1 text-end" style={{ width: "50px" }}>Qtd</div>
                <div className="mx-1 text-end" style={{ width: "70px" }}>Unit.</div>
                <div className="mx-1 text-end" style={{ width: "80px" }}>Total</div>
                <div className="flex-grow-1 mx-1" style={{ minWidth: "120px" }}>Cliente</div>
                <div className="mx-1" style={{ width: "90px" }}>Empresa</div>
                <div className="mx-1" style={{ width: "70px" }}>Núm.</div>
                <div style={{ width: "80px" }}>Vendedor</div>
            </div>
        );

        return (
            <>
                {/* Resumo */}
                {allVendas.length > 0 && (
                    <div className="row g-3 mb-3">
                        <div className="col-md-4">
                            <div className="card bg-primary text-white">
                                <div className="card-body text-center">
                                    <FontAwesomeIcon icon={faShoppingCart} size="2x" className="mb-2" />
                                    <h5 className="card-title mb-1">{totalVendido}</h5>
                                    <p className="card-text">Unidades Vendidas</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card bg-success text-white">
                                <div className="card-body text-center">
                                    <FontAwesomeIcon icon={faDollarSign} size="2x" className="mb-2" />
                                    <h5 className="card-title mb-1">
                                        {new Intl.NumberFormat('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL'
                                        }).format(valorTotalVendido)}
                                    </h5>
                                    <p className="card-text">Valor Total</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card bg-info text-white">
                                <div className="card-body text-center">
                                    <FontAwesomeIcon icon={faFileInvoiceDollar} size="2x" className="mb-2" />
                                    <h5 className="card-title mb-1">{totalCount}</h5>
                                    <p className="card-text">Total de Vendas</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!isMobile && (
                    <div style={{ overflowX: "auto" }}>
                        {tableHeader}
                    </div>
                )}
                <div style={{
                    height: "400px",
                    border: "1px solid var(--phoenix-border-color)",
                    overflowX: isMobile ? "hidden" : "auto"
                }}>
                    <InfiniteLoader
                        isItemLoaded={(index) => !!allVendas[index]}
                        itemCount={itemCount}
                        loadMoreItems={loadMoreItems}
                        threshold={10} // Carrega mais quando estiver a 10 itens do fim
                    >
                        {({ onItemsRendered, ref }) => (
                            <List
                                ref={ref}
                                height={400}
                                width="100%"
                                itemCount={itemCount}
                                itemSize={isMobile ? 120 : 80} // Altura ajustada para mobile para coincidir com outros históricos
                                onItemsRendered={onItemsRendered}
                                itemData={{
                                    items: allVendas,
                                    loadMoreItems,
                                    hasNextPage: !!hasNextPage,
                                    isLoading: isFetchingNextPage
                                }}
                            >
                                {VendaRow}
                            </List>
                        )}
                    </InfiniteLoader>
                </div>

                {totalCount > 0 && (
                    <div className="text-center mt-3">
                        <small className="text-body-tertiary">
                            Mostrando {allVendas.length} de {totalCount} vendas
                            {isFetchingNextPage && " • Carregando mais..."}
                        </small>
                    </div>
                )}
            </>
        );
    };

    return (
        <Card className="mb-3">
            <Accordion>
                <Accordion.Item eventKey="0">
                    <Accordion.Header onClick={handleToggle}>
                        <div className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faShoppingCart} className="text-success" />
                            <span>Histórico de Vendas</span>
                            {totalCount > 0 && (
                                <Badge bg="success" pill className="ms-2">
                                    {totalCount}
                                </Badge>
                            )}
                            {totalVendido > 0 && (
                                <Badge bg="primary" pill className="ms-1">
                                    {totalVendido} unidades
                                </Badge>
                            )}
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        {isExpanded && renderContent()}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </Card>
    );
};

export default HistoricoVendasTab; 